import { useEffect, useState, useContext, useRef, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import UserDetailsPopup from "./UserDetailsPopup";
import { fetchHostPastEvents, guestsFromEvents } from "../Events/utils/pastEventGuests";
import { GlobalContext } from "../context/GlobalContext";
import { useUserSelection } from "../hooks/useUserSelection";
import {
  UserMasonryGallery,
  UserSelectionConfirmModal,
  EnlargedPhotoModal,
} from "../components/UserGallery";

const USERS_PER_PAGE = 20;
const INITIAL_PAGES = 2;
const PRELOAD_MARGIN = "800px"; // Load more when within ~2 pages of the bottom

const COMPARISON_OPS = [
  { op: ">=", test: (a, b) => Number(a) >= b },
  { op: "<=", test: (a, b) => Number(a) <= b },
  { op: ">",  test: (a, b) => Number(a) > b },
  { op: "<",  test: (a, b) => Number(a) < b },
];

const resolveNestedValue = (obj, path) =>
  path.split(".").reduce((curr, key) => curr?.[key], obj);

const containsString = (obj, lower) => {
  if (!obj || typeof obj !== "object") return false;
  return Object.values(obj).some((v) => {
    if (typeof v === "string") return v.toLowerCase().includes(lower);
    if (typeof v === "object") return containsString(v, lower);
    return false;
  });
};

const extractFieldPaths = (obj, prefix = "", depth = 0, paths = new Map()) => {
  if (depth > 4 || !obj || typeof obj !== "object" || Array.isArray(obj)) return paths;
  Object.entries(obj).forEach(([key, val]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    paths.set(path, (paths.get(path) || 0) + 1);
    if (val && typeof val === "object" && !Array.isArray(val)) {
      extractFieldPaths(val, path, depth + 1, paths);
    }
  });
  return paths;
};

const hasOperator = (q) =>
  [">=", "<=", ">", "<", ":"].some((op) => q.includes(op));

export default function Users() {
  const { globalState, setGlobalState } = useContext(GlobalContext);
  const [allUsers, setAllUsers] = useState([]);
  const [displayedUsers, setDisplayedUsers] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [enlargedPhotoUser, setEnlargedPhotoUser] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [userSource, setUserSource] = useState("all"); // "all" or "pastEvents" - only for super admins
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") ?? "");
  const [searchFocused, setSearchFocused] = useState(false);
  const [suggestionIndex, setSuggestionIndex] = useState(-1);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q !== null) setSearchQuery(q);
  }, [searchParams]);

  const loadMoreRef = useRef(null);

  const {
    selectedUsers,
    isSelectionMode,
    handleToggleSelectionMode,
    handleToggleUserSelection,
    isUserSelected,
    clearSelection,
  } = useUserSelection();

  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim();
    if (!q) return null;

    for (const { op, test } of COMPARISON_OPS) {
      const idx = q.indexOf(op);
      if (idx !== -1) {
        const path = q.slice(0, idx).trim();
        const value = parseFloat(q.slice(idx + op.length).trim());
        if (path && !isNaN(value)) {
          return allUsers.filter((user) => {
            const fieldValue = resolveNestedValue(user, path);
            return fieldValue != null && test(fieldValue, value);
          });
        }
      }
    }

    if (q.includes(":")) {
      const colonIdx = q.indexOf(":");
      const path = q.slice(0, colonIdx).trim();
      const value = q.slice(colonIdx + 1).trim().toLowerCase();
      return allUsers.filter((user) => {
        const fieldValue = resolveNestedValue(user, path);
        if (value === "null" || value === "undefined") return fieldValue == null;
        if (fieldValue == null) return false;
        return String(fieldValue).toLowerCase() === value;
      });
    }

    const lower = q.toLowerCase();
    return allUsers.filter(
      (user) =>
        user.id?.toLowerCase().includes(lower) || containsString(user, lower)
    );
  }, [searchQuery, allUsers]);

  const fieldPaths = useMemo(() => {
    const paths = new Map();
    allUsers.slice(0, 100).forEach(({ id, ...rest }) => extractFieldPaths(rest, "", 0, paths));
    return Array.from(paths.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 60)
      .map(([path]) => path);
  }, [allUsers]);

  const suggestions = useMemo(() => {
    const q = searchQuery.trim();
    if (!q || hasOperator(q)) return [];
    const lower = q.toLowerCase();
    return fieldPaths.filter((p) => p.toLowerCase().includes(lower)).slice(0, 8);
  }, [searchQuery, fieldPaths]);

  const showSuggestions = searchFocused && suggestions.length > 0;

  // Fetch all users from Firestore
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        // Super admin with "all" source: fetch all users from users collection
        if (globalState.isSuperAdmin && userSource === "all") {
          const userCollection = collection(db, "users");
          const querySnapshot = await getDocs(userCollection);
          const users = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          const initialCount = USERS_PER_PAGE * INITIAL_PAGES;
          setAllUsers(users);
          setDisplayedUsers(users.slice(0, initialCount));
          setHasMore(users.length > initialCount);
        } else {
          // Non-super admin OR super admin with "pastEvents" source: fetch users from events
          let pastEvents;
          if (globalState.isSuperAdmin && userSource === "pastEvents") {
            // Super admin: fetch all events
            const querySnapshot = await getDocs(collection(db, "events"));
            pastEvents = querySnapshot.docs.map((snap) => ({ id: snap.id, data: snap.data() }));
          } else {
            // Everyone else: the events they host or co-host (GIS-285)
            pastEvents = await fetchHostPastEvents(globalState.vendorID);
          }

          const users = guestsFromEvents(pastEvents);
          const initialCount = USERS_PER_PAGE * INITIAL_PAGES;
          setAllUsers(users);
          setDisplayedUsers(users.slice(0, initialCount));
          setHasMore(users.length > initialCount);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchAllUsers();
  }, [globalState.isSuperAdmin, globalState.vendorID, userSource]);

  // Load more users for infinite scroll
  const loadMoreUsers = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);

    const currentLength = displayedUsers.length;
    const nextBatch = allUsers.slice(currentLength, currentLength + USERS_PER_PAGE);

    if (nextBatch.length > 0) {
      setDisplayedUsers((prev) => [...prev, ...nextBatch]);
      setHasMore(currentLength + nextBatch.length < allUsers.length);
    } else {
      setHasMore(false);
    }
    setIsLoadingMore(false);
  }, [allUsers, displayedUsers.length, hasMore, isLoadingMore]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMoreUsers();
        }
      },
      { threshold: 0.1, rootMargin: PRELOAD_MARGIN }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, isLoadingMore, loadMoreUsers]);

  const handleRowClick = (user) => {
    if (globalState.isSuperAdmin) {
      setSelectedUser(user);
      setGlobalState((prev) => ({ ...prev, sidebarCollapsed: true }));
    } else {
      setEnlargedPhotoUser(user);
    }
  };

  const handleClosePopup = () => {
    setSelectedUser(null);
    setGlobalState((prev) => ({ ...prev, sidebarCollapsed: false }));
  };

  const handleCloseEnlargedPhoto = () => {
    setEnlargedPhotoUser(null);
  };

  const handleCardClick = (user) => {
    if (isSelectionMode) {
      handleToggleUserSelection(user);
    } else {
      handleRowClick(user);
    }
  };

  const handleConfirmInvite = () => {
    setShowConfirmModal(false);
    clearSelection();
  };

  return (
    <div
      className={`px-4 sm:px-6 lg:px-8 transition-all duration-300 ease-in-out ${selectedUser ? 'lg:pr-[29rem]' : ''}`}
    >
      <div className="flex mt-4 sm:mt-0 justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Users</h1>
        <div className="flex gap-2">
          {isSelectionMode && selectedUsers.length > 0 && (
            <button
              onClick={() => setShowConfirmModal(true)}
              className="px-4 py-2 rounded-md font-medium bg-primary text-white transition-colors"
            >
              Send Invite ({selectedUsers.length})
            </button>
          )}
          <button
            onClick={handleToggleSelectionMode}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              isSelectionMode
                ? "bg-gray-500 text-white hover:bg-gray-600"
                : "bg-primary text-white hover:bg-primaryDark"
            }`}
          >
            {isSelectionMode ? "Cancel" : "Invite"}
          </button>
        </div>
      </div>

      {globalState.isSuperAdmin && (
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg mt-4">
          <button
            onClick={() => setUserSource("all")}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              userSource === "all"
                ? "bg-white dark:bg-gray-800 shadow text-gray-900 dark:text-gray-100"
                : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200"
            }`}
          >
            All Users
          </button>
          <button
            onClick={() => setUserSource("pastEvents")}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              userSource === "pastEvents"
                ? "bg-white dark:bg-gray-800 shadow text-gray-900 dark:text-gray-100"
                : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200"
            }`}
          >
            Past Events
          </button>
        </div>
      )}

      <div className="mt-4 relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setSuggestionIndex(-1); }}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          onKeyDown={(e) => {
            if (!showSuggestions) return;
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setSuggestionIndex((i) => Math.min(i + 1, suggestions.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setSuggestionIndex((i) => Math.max(i - 1, -1));
            } else if (e.key === "Enter" && suggestionIndex >= 0) {
              e.preventDefault();
              setSearchQuery(suggestions[suggestionIndex]);
              setSuggestionIndex(-1);
              setSearchFocused(false);
            } else if (e.key === "Escape") {
              setSearchFocused(false);
            }
          }}
          placeholder="Search: ID, field:value, field.sub:value, field>5, field<=10"
          className="w-full px-4 py-2 pr-9 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 text-lg leading-none"
          >
            ×
          </button>
        )}
        {showSuggestions && (
          <ul className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-56 overflow-auto">
            {suggestions.map((path, i) => (
              <li
                key={path}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setSearchQuery(path);
                  setSuggestionIndex(-1);
                  setSearchFocused(false);
                }}
                className={`px-4 py-2 text-sm font-mono cursor-pointer ${
                  i === suggestionIndex
                    ? "bg-primary text-white"
                    : "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                {path}
              </li>
            ))}
          </ul>
        )}
      </div>
      {filteredUsers && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {filteredUsers.length} result{filteredUsers.length !== 1 ? "s" : ""}
        </p>
      )}

      <UserMasonryGallery
        users={filteredUsers ?? displayedUsers}
        isSelectionMode={isSelectionMode}
        isUserSelected={isUserSelected}
        onUserClick={handleCardClick}
        onExpandPhoto={isSelectionMode ? setEnlargedPhotoUser : undefined}
        loadMoreRef={filteredUsers ? undefined : loadMoreRef}
        isLoadingMore={filteredUsers ? false : isLoadingMore}
        hasMore={filteredUsers ? false : hasMore}
        totalCount={filteredUsers ? filteredUsers.length : allUsers.length}
      />

      {selectedUser && (
        <UserDetailsPopup user={selectedUser} onClose={handleClosePopup} />
      )}

      <UserSelectionConfirmModal
        isOpen={showConfirmModal}
        selectedUsers={selectedUsers}
        onConfirm={handleConfirmInvite}
        onCancel={() => setShowConfirmModal(false)}
      />

      <EnlargedPhotoModal
        user={enlargedPhotoUser}
        onClose={handleCloseEnlargedPhoto}
      />
    </div>
  );
}
